import type { PaginatedData } from "@/nzds/data-grid"
import type { SpraypaintQueryOptions } from "@/services/types"
import type { HttpError } from "@/utils/api"
import { resolveQueryThrowOnError } from "@/utils/queryClient"
import type { QueryKey, UseInfiniteQueryOptions } from "@tanstack/react-query"
import { useInfiniteQuery } from "@tanstack/react-query"

interface BaseRecord {
  id: number | string
}

export type SpraypaintInfiniteRecord<TRecord> = TRecord & BaseRecord

interface UseSpraypaintInfiniteQueryOptions<TRecord extends BaseRecord> {
  defaultPageSize: number
  fetchPage: (
    options: SpraypaintQueryOptions
  ) => Promise<PaginatedData<TRecord>>
  initialPage?: number
  apiQueryOptions?: SpraypaintQueryOptions
  queryKey: QueryKey
  queryOptions?: Omit<
    UseInfiniteQueryOptions<PaginatedData<TRecord>, HttpError>,
    | "queryKey"
    | "queryFn"
    | "getNextPageParam"
    | "getPreviousPageParam"
    | "initialPageParam"
    | "select"
  >
}

export const useSpraypaintInfiniteQuery = <TRecord extends BaseRecord>({
  defaultPageSize,
  fetchPage,
  initialPage,
  apiQueryOptions = {},
  queryKey,
  queryOptions = {},
}: UseSpraypaintInfiniteQueryOptions<TRecord>) => {
  return useInfiniteQuery<PaginatedData<TRecord>, HttpError>({
    ...queryOptions,
    queryKey,
    queryFn: ({ pageParam }) => {
      const requestedPage = Number(pageParam)
      const requestedPageSize = apiQueryOptions.pageSize ?? defaultPageSize

      return fetchPage({
        ...apiQueryOptions,
        page: requestedPage,
        pageSize: requestedPageSize,
      })
    },
    initialPageParam: initialPage ?? apiQueryOptions.page ?? 1,
    getNextPageParam: (lastPage) => {
      if (lastPage.page >= lastPage.pages) {
        return undefined
      }
      return lastPage.page + 1
    },
    getPreviousPageParam: (firstPage) => {
      if (firstPage.page <= 1) {
        return undefined
      }
      return firstPage.page - 1
    },
    throwOnError: resolveQueryThrowOnError(queryOptions.throwOnError),
  })
}
